// Central content file for the Banga Pay marketing website.
// Items marked NEEDS CONFIRMATION must be verified by the Banga Pay team before launch.

export const site = {
  brand: {
    name: "Banga Pay",
    tagline: "Simple payments for everyday life.",
    url: "https://bangainvestmentsug.com/",
    // NEEDS CONFIRMATION: exact legal entity name and any regulator/licence details.
    legalName: "",
    regulatoryInfo: "",
  },

  contact: {
    email: "info@bangainvestmentsug.com",
    address: "Banga House, Namugongo",
    // NEEDS CONFIRMATION: phone / WhatsApp line.
    phone: "",
  },

  // NEEDS CONFIRMATION: official social accounts.
  socials: [],

  nav: [
    { label: "Features", href: "#features", testid: "nav-features-link" },
    { label: "How It Works", href: "#how-it-works", testid: "nav-how-it-works-link" },
    { label: "Security", href: "#security", testid: "nav-security-link" },
    { label: "FAQs", href: "#faqs", testid: "nav-faqs-link" },
  ],

  hero: {
    eyebrow: "Mobile payments, made straightforward",
    headlineLines: ["Payments made simple", "with Banga Pay."],
    subcopy:
      "Banga Pay brings everyday payments together in one mobile app. Send money, pay bills and keep track of your transactions from your phone.",
    primaryCta: { label: "Get Banga Pay", href: "#download", testid: "hero-primary-cta" },
    secondaryCta: { label: "Learn More", href: "#features", testid: "hero-secondary-cta" },
  },

  marquee: ["Send money", "Receive payments", "Pay bills", "Mobile money", "Transaction history", "Account management"],

  valueStrip: [
    { icon: "Feather", title: "Simple", text: "Easy payments without unnecessary complexity." },
    { icon: "Zap", title: "Fast", text: "Designed to make everyday transactions straightforward." },
    { icon: "Smartphone", title: "Accessible", text: "Manage payments directly from your phone." },
    { icon: "ShieldCheck", title: "Secure", text: "Security built into every transaction." },
  ],

  features: {
    eyebrow: "01 · Features",
    heading: "Everything you need to make payments simpler",
    subcopy: "Banga Pay focuses on the essentials: the payment tools people actually use, in one place.",
    items: [
      { icon: "ArrowUpRight", title: "Send Money", text: "Send money quickly and conveniently from the Banga Pay app." },
      { icon: "ArrowDownLeft", title: "Receive Money", text: "Receive payments directly through your Banga Pay account." },
      { icon: "Receipt", title: "Pay Bills", text: "Handle supported bills and everyday payments from one place." },
      { icon: "Smartphone", title: "Mobile Money", text: "Connect Banga Pay to supported mobile money services where available." },
      { icon: "History", title: "Transaction History", text: "See your recent payments and keep track of every transaction." },
      { icon: "UserRound", title: "Account Management", text: "Manage your Banga Pay profile and payment activity from the app." },
    ],
  },

  showcase: {
    eyebrow: "02 · The App",
    heading: "Everything from one simple app.",
    subcopy:
      "A quick look at the Banga Pay experience: your balance, your payments and your history, organised so they are easy to find.",
    screens: [
      { id: "home", caption: "Home" },
      { id: "send", caption: "Send money" },
      { id: "history", caption: "Transaction history" },
    ],
    placeholderNote:
      "Screens shown are placeholders. They will be replaced with actual Banga Pay app screenshots.",
  },

  howItWorks: {
    eyebrow: "03 · How It Works",
    heading: "Getting started is simple",
    steps: [
      {
        number: "1",
        title: "Download Banga Pay",
        text: "The Banga Pay app is coming soon. Download details will be shared here when launch is ready.",
      },
      {
        number: "2",
        title: "Create your account",
        text: "Register with your details and complete the account verification steps shown in the app. Verification may take a short time to complete.",
      },
      {
        number: "3",
        title: "Start using Banga Pay",
        text: "Once your account is active, you can access the payment services available to you.",
      },
    ],
  },

  why: {
    eyebrow: "04 · Why Banga Pay",
    heading: "Built for everyday payments",
    paragraphs: [
      "Most of us move money on our phones every day. Paying a bill, sending money to family, settling with a supplier. Banga Pay brings those everyday payments together in one simple app.",
      "No clutter and no confusion. Just the payment tools you need, designed to work the way you expect them to.",
    ],
  },

  // NEEDS CONFIRMATION: keep these points aligned with the security measures actually implemented in the app.
  security: {
    eyebrow: "05 · Security",
    heading: "Your security matters",
    intro:
      "Every Banga Pay account is protected by the security features built into the app. You confirm your identity when you sign in, and you approve every payment before it goes through.",
    items: [
      { icon: "KeyRound", title: "Secure sign-in", text: "Your account is protected by your own login credentials and PIN." },
      { icon: "BadgeCheck", title: "Account verification", text: "New accounts complete a verification process during registration." },
      { icon: "CheckCircle2", title: "Transaction confirmation", text: "You review and approve each payment before it is sent." },
      { icon: "Mail", title: "Support when you need it", text: "Questions about your account? Our team is one email away." },
    ],
  },

  faqs: {
    eyebrow: "06 · FAQs",
    heading: "Frequently asked questions",
    items: [
      {
        q: "What is Banga Pay?",
        a: "Banga Pay is a mobile payments app that lets you send and receive money, pay bills and manage everyday transactions from your phone.",
      },
      {
        q: "How do I create a Banga Pay account?",
        a: "When the app is available, follow the registration steps shown in Banga Pay. You will be asked for your details and to complete an account verification process.",
      },
      {
        q: "What can I use Banga Pay for?",
        a: "Sending money, receiving payments, paying supported bills and keeping track of your transaction history, all from one app.",
      },
      {
        q: "How do I add money to my account?",
        // NEEDS CONFIRMATION: list the actual deposit channels once confirmed.
        a: "The deposit options available to you are shown in the app. Where supported, you can add money through mobile money services.",
      },
      {
        q: "How do I withdraw money?",
        // NEEDS CONFIRMATION: list the actual withdrawal channels once confirmed.
        a: "Withdrawal options are shown in the app and depend on the services available to your account.",
      },
      {
        q: "Is Banga Pay secure?",
        a: "Your account is protected by your login credentials and PIN, and every transaction must be confirmed by you before it is processed.",
      },
      {
        q: "Are there transaction fees?",
        a: "Any applicable fees are shown in the app before you confirm a transaction, so you always know the cost upfront.",
      },
      {
        q: "Where can I download Banga Pay?",
        // NEEDS CONFIRMATION: update once the store listings are live.
        a: "The Banga Pay app is coming soon. Download details will be published on this page when launch is ready.",
      },
    ],
  },

  finalCta: {
    heading: "Banga Pay is coming soon.",
    subcopy: "Download details will be shared here when the app is ready for launch.",
  },

  contactPage: {
    eyebrow: "Contact us",
    title: "Talk to the Banga Pay team",
    intro:
      "For account questions, partnership enquiries or support with Banga Pay, contact our team using the details below.",
    emailLabel: "Email",
    locationLabel: "Location",
    responseNote: "We aim to respond to customer and business enquiries as soon as possible.",
    ctaLabel: "Email our team",
  },

  legal: {
    lastUpdated: "10th July 2026",
    privacy: {
      title: "Privacy Policy",
      intro:
        "This Privacy Policy explains how Banga Pay may collect, use, disclose, retain and protect personal information when you visit this website, create an account or use our payment services.",
      sections: [
        {
          heading: "Account and Identity Information",
          paragraphs: [
            "We may collect your name, phone number, email address, date of birth, address, account credentials and other details needed to create and manage a Banga Pay account.",
            "Where verification is required, we may collect identification document details, photographs, selfie checks, business details, tax or registration information and other know-your-customer information needed to confirm your identity or eligibility.",
          ],
        },
        {
          heading: "Transaction and Financial Information",
          paragraphs: [
            "We may collect transaction records, wallet activity, payment references, biller details, mobile money or bank account references, device approvals, fees, reversals, failed payment attempts and customer support records.",
            "We use transaction information to process payments, display balances and history, confirm payment status, resolve disputes, prevent fraud and meet record-keeping obligations.",
          ],
        },
        {
          heading: "Device, Security and Usage Information",
          paragraphs: [
            "We may collect device identifiers, IP address, browser or app version, operating system, login times, approximate location, network information, cookies, analytics events and security signals.",
            "We use this information to protect accounts, detect suspicious activity, troubleshoot service issues, improve performance and maintain the security of Banga Pay services.",
          ],
        },
        {
          heading: "How We Use Your Information",
          paragraphs: [
            "We use personal information to open and verify accounts, provide payment services, process transactions, calculate and display fees, send service notices, respond to support requests and improve our products.",
            "We may also use information for fraud monitoring, risk scoring, transaction screening, anti-money-laundering checks, sanctions screening, audit, reporting and compliance with applicable laws or lawful requests.",
          ],
        },
        {
          heading: "When We Share Information",
          paragraphs: [
            "We may share information with payment processors, mobile money providers, banks, card networks, billers, identity verification providers, fraud prevention providers, cloud and messaging providers, professional advisers and support vendors where needed to operate Banga Pay.",
            "We may disclose information to regulators, courts, law enforcement agencies or other authorised bodies where required by law, compliance obligations, dispute handling or protection of our users and services. We do not sell personal information.",
          ],
        },
        {
          heading: "Data Security and Retention",
          paragraphs: [
            "We use reasonable technical and organisational measures designed to protect personal information against unauthorised access, loss, alteration or misuse. No electronic system is completely secure, so you should keep your device, PIN and login details protected.",
            "We retain personal information for as long as needed to provide services, maintain transaction records, resolve disputes, prevent fraud, meet audit requirements and comply with legal, tax, accounting and regulatory obligations.",
          ],
        },
        {
          heading: "Your Rights and Choices",
          paragraphs: [
            "You may contact us to request access to, correction of or deletion of your personal information where applicable law allows. We may need to verify your identity before processing a request.",
            "Some information may need to be retained even after an account is closed, including transaction, compliance, fraud prevention, dispute and legal records.",
          ],
        },
        {
          heading: "Minors",
          paragraphs: [
            "Banga Pay payment services are intended for users who are legally permitted to use financial services. We do not knowingly provide payment accounts to minors unless permitted by applicable law and appropriate consent or authorisation is in place.",
          ],
        },
        {
          heading: "Contact",
          paragraphs: [
            "Questions about this Privacy Policy can be sent to info@bangainvestmentsug.com or delivered to Banga House, Namugongo.",
          ],
        },
      ],
    },
    terms: {
      title: "Terms and Conditions",
      intro:
        "These Terms and Conditions set out the basic rules for using this website and Banga Pay payment services. By creating an account or using the services, you agree to these terms.",
      sections: [
        {
          heading: "Eligibility and Account Registration",
          paragraphs: [
            "You must provide accurate, current and complete information when registering for Banga Pay and keep that information updated.",
            "We may require identity, address, business, source-of-funds or other verification information before enabling some features, increasing limits or processing certain transactions.",
          ],
        },
        {
          heading: "Security of Your Account",
          paragraphs: [
            "You are responsible for keeping your phone, login details, OTPs, PINs, passwords and authentication methods secure. Do not share them with anyone.",
            "You must notify us promptly if your device is lost, your credentials are exposed or you suspect unauthorised account activity. We may block or restrict access while we investigate.",
          ],
        },
        {
          heading: "Payments, Wallets and Transaction Instructions",
          paragraphs: [
            "You are responsible for checking recipient details, biller details, amounts, fees and any transaction summary before you confirm a payment. A confirmed instruction authorises Banga Pay and its partners to process the transaction.",
            "Transaction status may depend on mobile money networks, banks, billers, payment processors and other third-party systems. Some transactions may be delayed, declined, reversed or unavailable because of partner limits, network issues, compliance checks or incorrect details.",
          ],
        },
        {
          heading: "Fees, Limits and Reversals",
          paragraphs: [
            "Fees, charges, exchange rates where applicable and transaction limits may vary by service, channel, partner, account type, verification level and applicable law. You should review any fees shown before confirming a transaction.",
            "Reversals, refunds and chargebacks are not guaranteed. They may depend on the type of transaction, the recipient, the payment partner, available funds, timing and applicable rules.",
          ],
        },
        {
          heading: "Compliance, Monitoring and Restrictions",
          paragraphs: [
            "You must not use Banga Pay for fraud, money laundering, terrorism financing, illegal gambling, scams, unauthorised lending, prohibited goods or services, sanctions evasion or any activity that violates applicable law.",
            "We may monitor, screen, delay, decline, reverse, limit, suspend or close transactions or accounts where required for risk management, fraud prevention, regulatory compliance, partner requirements, court orders or lawful requests.",
          ],
        },
        {
          heading: "Third-Party Providers",
          paragraphs: [
            "Banga Pay may connect with mobile money operators, banks, billers, payment processors, identity verification providers and other third-party services. Their rules, limits, fees and availability may apply in addition to these terms.",
            "We are not responsible for delays, outages, fees, reversals or errors caused by third-party systems outside our reasonable control.",
          ],
        },
        {
          heading: "Service Availability and Changes",
          paragraphs: [
            "We aim to keep Banga Pay available, but we do not guarantee uninterrupted or error-free service. Maintenance, updates, security events, network outages, partner downtime or events beyond our control may affect availability.",
            "We may update, add, suspend or remove features, channels, limits or requirements from time to time. We may update these Terms and Conditions by posting a revised version on this page.",
          ],
        },
        {
          heading: "Disputes and Customer Support",
          paragraphs: [
            "If you believe a transaction is incorrect or unauthorised, contact us as soon as possible with the transaction reference, date, amount and any supporting information.",
            "We will review support requests using available records and partner information. Resolution times may depend on the payment channel, third-party provider and nature of the issue.",
          ],
        },
        {
          heading: "No Financial Advice",
          paragraphs: [
            "Information on this website or in the app is provided for general service use and does not constitute financial, investment, tax or legal advice.",
          ],
        },
        {
          heading: "Contact",
          paragraphs: [
            "Questions about these Terms and Conditions can be sent to info@bangainvestmentsug.com or delivered to Banga House, Namugongo.",
          ],
        },
      ],
    },
  },

  seo: {
    title: "Banga Pay | Simple Digital Payments",
    description:
      "Banga Pay makes it simple to send, receive and manage everyday payments from your phone.",
  },
};
